// 8 conditions Sano treats — each gets a dedicated SEO page at /conditions/:slug

export const CONDITIONS = [
  {
    slug: "stress-anxiety",
    title: "Stress & Anxiety",
    eyebrow: "Nervous System Care",
    metaTitle: "Acupuncture for Stress and Anxiety in NYC | Sano Acupuncture Bronx",
    metaDescription:
      "Calm an overactive nervous system with acupuncture in the Bronx. Joannie Campuzano, L.Ac. specializes in stress, anxiety, panic and burnout — over ten years of practice.",
    hero:
      "When the body forgets how to feel safe, everything tightens. We help it remember.",
    intro:
      "Chronic stress is not a personality. It is a nervous system that has learned to expect threat, and never received the signal to stand down. Acupuncture sends that signal — through the vagus nerve, through specific points that calm the heart and clear the spirit — so the body can begin to soften without effort.",
    symptoms: [
      "Racing thoughts, especially at night",
      "Chest tightness, shallow breathing",
      "Irritability, short fuse, easily overwhelmed",
      "Burnout, emotional numbness, feeling 'tapped out'",
      "Panic episodes or anxious anticipation",
    ],
    howItHelps: [
      "Acupuncture has been shown to lower cortisol and shift the body into parasympathetic dominance within a single session.",
      "Points like Yintang, HT 7 (Shen Men) and PC 6 quiet the heart and downregulate the fight-or-flight response.",
      "Over a series of treatments, the resting baseline of the nervous system itself begins to recalibrate.",
      "We pair acupuncture with gentle take-home practices — breath, acupressure, food — so progress continues between visits.",
    ],
    plan:
      "Most clients begin with weekly sessions for 4–6 weeks, then taper to maintenance every 2–4 weeks. Many report 'I finally feel like myself again' within the first month.",
    image:
      "https://images.unsplash.com/photo-1518091043644-c1d4457512c6?fm=jpg&q=80&w=1600&auto=format&fit=crop",
  },
  {
    slug: "neck-back-pain",
    title: "Neck & Back Pain",
    eyebrow: "Pain Relief",
    metaTitle: "Acupuncture for Neck & Back Pain in NYC | Sano Acupuncture",
    metaDescription:
      "Effective, drug-free relief for chronic neck and back pain. Acupuncture, cupping and trigger-point release in the Bronx with Joannie Campuzano, L.Ac.",
    hero: "Pain is the body asking for attention. We listen — and answer in kind.",
    intro:
      "Whether it's the desk-bound tension at the base of the skull, the low back that 'goes out' once a season, or the chronic tightness no stretch seems to touch, acupuncture works at the level of the fascia, the nerves, and the muscle itself. Combined with cupping, the work goes deeper than surface massage can reach.",
    symptoms: [
      "Tension headaches at the base of the skull",
      "Stiff neck, limited rotation",
      "Tight trapezius, knots between the shoulder blades",
      "Low back pain — chronic or acute flare-ups",
      "Sciatic pain radiating into the leg",
    ],
    howItHelps: [
      "Acupuncture stimulates endorphin release and modulates the nervous system's pain signaling — clinically proven, especially for chronic pain.",
      "Trigger-point needling directly releases the tight bands of muscle that referred pain originates from.",
      "Cupping decompresses fascia and increases blood flow, often providing immediate relief.",
      "We address the cause (posture, stress patterns, breath) as well as the symptom — so the pain stays gone.",
    ],
    plan:
      "Acute pain often responds within 2–3 sessions. Chronic patterns typically take 6–10 visits paired with home stretches. Insurance often covers acupuncture for documented chronic pain.",
    image:
      "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?fm=jpg&q=80&w=1600&auto=format&fit=crop",
  },
  {
    slug: "migraines",
    title: "Migraines & Headaches",
    eyebrow: "Pain Relief",
    metaTitle: "Acupuncture for Migraines and Headaches NYC | Sano Acupuncture Bronx",
    metaDescription:
      "Reduce migraine frequency and severity with acupuncture in the Bronx. Cochrane-reviewed evidence-based care with Joannie Campuzano, L.Ac.",
    hero: "Fewer migraines. Less severity. Real, measurable change.",
    intro:
      "Acupuncture is one of the most well-studied alternatives for migraine prevention — the Cochrane review found it at least as effective as preventive medication, with far fewer side effects. We treat both the acute attack and the underlying patterns that bring migraines back.",
    symptoms: [
      "Recurring migraines with aura or without",
      "Tension headaches at the temples or forehead",
      "Cervicogenic headaches starting at the neck",
      "Hormonal or menstrual-cycle migraines",
      "Light, sound or smell sensitivity",
    ],
    howItHelps: [
      "Multiple studies show acupuncture reduces migraine frequency and intensity comparably to first-line preventive medications.",
      "Treatment targets the trigeminal nerve, the cervical fascia, and the constitutional patterns (Liver Qi stagnation, Blood deficiency) that predispose the body to migraines.",
      "Many clients reduce or eliminate triptan use over a course of treatment.",
      "We also identify dietary and stress triggers and build a take-home protocol around them.",
    ],
    plan:
      "Weekly acupuncture for 8–10 weeks is the most studied protocol. Most clients see a 50–60% reduction in migraine frequency by week 6.",
    image:
      "https://images.unsplash.com/photo-1474418397713-7ede21d49118?fm=jpg&q=80&w=1600&auto=format&fit=crop",
  },
  {
    slug: "womens-health",
    title: "Women's Health",
    eyebrow: "Hormonal Balance",
    metaTitle: "Acupuncture for Women's Health & PMS in NYC | Sano Acupuncture",
    metaDescription:
      "Acupuncture for irregular cycles, painful periods, PMS, PCOS, perimenopause and hormonal balance in the Bronx. Trauma-informed care with Joannie Campuzano, L.Ac.",
    hero: "Your cycle is data. We help you read it — and rebalance.",
    intro:
      "Chinese medicine has refined the understanding of menstrual and hormonal health for over two thousand years. We work with painful periods, irregular cycles, PMS, perimenopause and PCOS — without bypassing what the body is trying to communicate.",
    symptoms: [
      "Painful or heavy periods",
      "Irregular or missing cycles",
      "PMS, mood swings, bloating, breast tenderness",
      "Perimenopausal hot flashes, sleep loss, anxiety",
      "PCOS symptoms, cystic acne, insulin resistance",
    ],
    howItHelps: [
      "Acupuncture regulates the HPO (hypothalamic-pituitary-ovarian) axis that governs hormonal cycles.",
      "Specific points and constitutional treatment can shift heavy bleeding, dysmenorrhea and PMS within 2–3 cycles.",
      "Treatment is mapped to your individual cycle phase — follicular, ovulatory, luteal, menstrual — for maximum effect.",
      "Custom herbal formulas and lifestyle guidance support what the needles begin.",
    ],
    plan:
      "Most women's health protocols span 3 cycles (about 12 weeks) of weekly or twice-weekly treatment, with shifts often visible by cycle 2.",
    image:
      "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?fm=jpg&q=80&w=1600&auto=format&fit=crop",
  },
  {
    slug: "fertility",
    title: "Fertility Support",
    eyebrow: "Reproductive Care",
    metaTitle: "Fertility Acupuncture in NYC | Sano Acupuncture Bronx",
    metaDescription:
      "Acupuncture to support natural conception and IVF/IUI cycles in the Bronx. Compassionate, evidence-informed fertility care with Joannie Campuzano, L.Ac.",
    hero: "A calmer body is a more fertile body.",
    intro:
      "Whether you are trying to conceive naturally or supporting IVF/IUI, acupuncture is one of the most studied complementary therapies in reproductive medicine. We work alongside your reproductive endocrinologist, never in opposition.",
    symptoms: [
      "Trying to conceive without success",
      "Irregular ovulation or anovulation",
      "Thin endometrial lining, poor egg quality concerns",
      "Recurrent pregnancy loss",
      "Preparing for or supporting IVF / IUI cycles",
    ],
    howItHelps: [
      "Acupuncture improves blood flow to the uterus and ovaries — a key factor in implantation and egg quality.",
      "Stress reduction during fertility journeys is itself fertility care; lowering cortisol supports reproductive hormones.",
      "For IVF, treatment is timed around stimulation, egg retrieval, and embryo transfer windows for best results.",
      "We hold space for the emotional reality of fertility work — there is room to be exactly where you are.",
    ],
    plan:
      "Pre-conception: 3 months of weekly treatment is the gold standard. Active IVF cycles: treatment timed around your protocol.",
    image:
      "https://images.unsplash.com/photo-1518627907093-2af2c44e30c8?fm=jpg&q=80&w=1600&auto=format&fit=crop",
  },
  {
    slug: "sleep",
    title: "Sleep Issues",
    eyebrow: "Restoration",
    metaTitle: "Acupuncture for Insomnia and Sleep in NYC | Sano Acupuncture",
    metaDescription:
      "Drug-free help for insomnia, restless sleep and night-time anxiety. Acupuncture in the Bronx with Joannie Campuzano, L.Ac.",
    hero: "When the mind won't quiet, the body has a key. We turn it.",
    intro:
      "Sleep loss is rarely just about sleep. It is about the nervous system unable to disengage, the heart unsettled, the mind on perpetual watch. Acupuncture works at the constitutional root — calming the spirit and restoring the body's natural circadian rhythm.",
    symptoms: [
      "Trouble falling asleep — racing mind at bedtime",
      "Waking between 1–3 AM and unable to return to sleep",
      "Restless, fragmented sleep",
      "Night sweats or hot flashes disrupting sleep",
      "Waking exhausted despite hours in bed",
    ],
    howItHelps: [
      "Acupuncture has been clinically shown to increase melatonin production and reduce nocturnal arousal.",
      "We treat the specific Chinese medicine pattern — Heart-Kidney disharmony, Liver fire, Spleen-Blood deficiency — driving your insomnia.",
      "Ear seeds extend the treatment between sessions, working on you while you sleep.",
      "Sleep hygiene and a pre-bed nervous system reset ritual are part of every plan.",
    ],
    plan:
      "Twice-weekly for 3 weeks, then weekly for 4–6 weeks. Most clients notice deeper sleep within the first 2–3 sessions.",
    image:
      "https://images.unsplash.com/photo-1520206183501-b80df61043c2?fm=jpg&q=80&w=1600&auto=format&fit=crop",
  },
  {
    slug: "digestive-health",
    title: "Digestive Health",
    eyebrow: "Gut Wellness",
    metaTitle: "Acupuncture for Digestion, IBS & Bloating in NYC | Sano Acupuncture",
    metaDescription:
      "Acupuncture for IBS, bloating, reflux, constipation and gut healing in the Bronx with Joannie Campuzano, L.Ac.",
    hero: "The gut listens to the nervous system. We talk to both.",
    intro:
      "When stress chronically dysregulates the vagus nerve, the digestive system suffers — bloating, IBS, reflux, irregular bowels. Acupuncture and Chinese herbal medicine address both the gut and the nervous system that governs it.",
    symptoms: [
      "Bloating and abdominal distension",
      "IBS — alternating constipation and diarrhea",
      "Acid reflux, heartburn, slow digestion",
      "Food sensitivities, intolerances",
      "Gut symptoms that worsen with stress",
    ],
    howItHelps: [
      "Acupuncture modulates the enteric nervous system and improves vagal tone, which directly impacts digestion.",
      "Specific points (ST 36, SP 6, CV 12) regulate motility, reduce inflammation, and support the spleen-stomach axis.",
      "Herbal formulas address dampness, deficiency, or stagnation — the three most common Chinese medicine root patterns.",
      "Together with stress reduction, most gut symptoms substantially improve within 6–8 weeks.",
    ],
    plan:
      "Weekly for 6–8 weeks, then monthly maintenance. Many clients pair this with a dietary review for fastest results.",
    image:
      "https://images.unsplash.com/photo-1490645935967-10de6ba17061?fm=jpg&q=80&w=1600&auto=format&fit=crop",
  },
  {
    slug: "facial-rejuvenation",
    title: "Facial Acupuncture",
    eyebrow: "Aesthetic & Restorative",
    metaTitle: "Facial Acupuncture in NYC | Sano Acupuncture Bronx",
    metaDescription:
      "Certified facial acupuncture for collagen, glow and natural rejuvenation in the Bronx. Joannie Campuzano, L.Ac.",
    hero: "Your face, brighter. Your body, calmer. Both at once.",
    intro:
      "Cosmetic acupuncture is restorative, not corrective. Tiny needles trigger micro-injuries that stimulate collagen and elastin production, while body points calm the nervous system that lives in your face — the jaw clench, the brow furrow, the held breath behind every expression.",
    symptoms: [
      "Fine lines, dullness, loss of firmness",
      "Jaw tension, TMJ symptoms",
      "Hormonal acne, cystic acne",
      "Puffiness, undereye darkness",
      "A face that simply looks tired",
    ],
    howItHelps: [
      "Acupuncture-induced collagen response is documented in peer-reviewed studies and visible after 5–10 sessions.",
      "Treatment includes gentle facial cupping and gua sha for circulation, lymph and instant glow.",
      "Body points are always included — because the face reflects what the body is holding.",
      "No fillers, no toxins, no downtime. The result is your face — softer, brighter, more rested.",
    ],
    plan:
      "Initial series: 8–10 weekly sessions for visible cumulative change. Then monthly maintenance to keep the glow.",
    image:
      "https://images.unsplash.com/photo-1583089580786-9dffd09b3e95?fm=jpg&q=80&w=1600&auto=format&fit=crop",
  },
];

export const CONDITIONS_BY_SLUG = CONDITIONS.reduce((a, c) => ({ ...a, [c.slug]: c }), {});
